import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api/client';
import { Section, SectionEyebrow, SectionTitle } from '../components/ui/Section';
import { PillButton } from '../components/ui/PillButton';
import { TextInput } from '../components/ui/TextInput';
import { Card } from '../components/ui/Card';
import { Chip } from '../components/ui/Chip';
import { useAuth } from '../lib/auth/store';
import { AI_PROVIDERS, type AIProvider, AREAS } from '@mindrift/shared';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsPage,
});

const PROVIDER_LABELS: Record<AIProvider, string> = {
  groq: 'Groq (default — no key needed)',
  openrouter: 'OpenRouter (user key)',
  deepseek: 'Deepseek (user key)',
};

const PROVIDER_HINTS: Record<AIProvider, string> = {
  groq: 'Uses the server-held key. You don\'t need to enter anything.',
  openrouter: 'Bring your own key from openrouter.ai',
  deepseek: 'Bring your own key from platform.deepseek.com',
};

function SettingsPage() {
  // const qc = useQueryClient();
  const settings = useAuth((s) => s.settings);
  const setSettings = useAuth((s) => s.setSettings);
  const [provider, setProvider] = useState<AIProvider>('groq');
  const [apiKey, setApiKey] = useState('');
  const [customAreas, setCustomAreas] = useState<string[]>([]);
  const [newArea, setNewArea] = useState('');
  const [defaultArea, setDefaultArea] = useState('Personal');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setProvider(settings.aiProvider);
      setCustomAreas(settings.customAreas);
      setDefaultArea(settings.defaultArea);
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () => api.settings.update({
      aiProvider: provider,
      aiApiKey: apiKey.trim() ? apiKey.trim() : null,
      customAreas,
      defaultArea,
    }),
    onSuccess: (res) => {
      setSettings(res);
      setApiKey('');
      setMessage('Saved.');
      setTimeout(() => setMessage(null), 2000);
    },
  });

  const allAreas = [...AREAS, ...customAreas];

  return (
    <Section surface="stone">
      <SectionEyebrow>SETTINGS</SectionEyebrow>
      <SectionTitle size="section-heading" className="mt-3">Tune your environment.</SectionTitle>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card>
          <p className="field-label">AI PROVIDER</p>
          <p className="text-caption text-muted-slate mt-1 mb-4">Used for journal tidy and journal-to-task.</p>
          <div className="flex flex-col gap-2">
            {AI_PROVIDERS.map((p) => (
              <button
                key={p}
                onClick={() => setProvider(p)}
                className={`text-left rounded-md border p-4 transition-all ${
                  provider === p ? 'border-near-black bg-canvas-white' : 'border-hairline bg-canvas-white hover:border-slate'
                }`}
                type="button"
              >
                <p className="text-body text-ink">{PROVIDER_LABELS[p]}</p>
                <p className="text-caption text-muted-slate mt-1">{PROVIDER_HINTS[p]}</p>
              </button>
            ))}
          </div>

          {provider !== 'groq' && (
            <div className="mt-5">
              <TextInput
                label={`${PROVIDER_LABELS[provider]} API key`}
                type="password"
                placeholder={settings?.hasApiKey ? '•••••••• (leave blank to keep current)' : 'Paste your key'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                hint="Encrypted with AES-256-GCM before storage."
              />
            </div>
          )}
        </Card>

        <Card>
          <p className="field-label">AREAS</p>
          <p className="text-caption text-muted-slate mt-1 mb-4">
            The 4 fixed tags are always available. Add your own below.
          </p>
          <div className="flex flex-wrap gap-2">
            {AREAS.map((a) => (
              <Chip key={a} tone="neutral" className="!cursor-default">{a}</Chip>
            ))}
            {customAreas.map((a) => (
              <button
                key={a}
                onClick={() => setCustomAreas(customAreas.filter((x) => x !== a))}
                className="group"
                title="Click to remove"
              >
                <Chip tone="coral">{a} ✕</Chip>
              </button>
            ))}
          </div>

          <div className="mt-5 flex gap-2">
            <input
              type="text"
              placeholder="Add custom area…"
              value={newArea}
              onChange={(e) => setNewArea(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const v = newArea.trim();
                  if (v && !allAreas.includes(v)) {
                    setCustomAreas([...customAreas, v]);
                    setNewArea('');
                  }
                }
              }}
              className="flex-1 rounded-md border border-hairline px-3 py-2.5 text-body"
            />
            <PillButton
              variant="outline"
              onClick={() => {
                const v = newArea.trim();
                if (v && !allAreas.includes(v)) {
                  setCustomAreas([...customAreas, v]);
                  setNewArea('');
                }
              }}
            >
              Add
            </PillButton>
          </div>

          <div className="mt-6">
            <p className="field-label">DEFAULT AREA</p>
            <select
              value={defaultArea}
              onChange={(e) => setDefaultArea(e.target.value)}
              className="mt-2 w-full rounded-md border border-hairline px-3 py-3 text-body bg-canvas-white"
            >
              {allAreas.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </Card>
      </div>

      <div className="mt-8 flex items-center gap-4">
        <PillButton onClick={() => save.mutate()} loading={save.isPending}>Save settings</PillButton>
        {message && <p className="text-caption text-deep-enterprise-green">{message}</p>}
      </div>
    </Section>
  );
}
